import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { toast } from 'react-hot-toast';
import { FaSearch, FaTimes, FaUserShield } from 'react-icons/fa';
import { db } from '../../../config/firebaseConfig';
import { CARGO_ACCESS_FIELD } from '../../../utils/companyShipments';
import styles from './CargoTracking.module.css';

const isActive = (employee) => employee.Active === 1 || employee.Active === '1';
const isAdmin = (employee) =>
  Array.isArray(employee.authorization) &&
  (employee.authorization.includes('admin') || employee.authorization.includes('Kısıtlı Admin'));
const fullName = (employee) => `${employee.eName || ''} ${employee.eSurname || ''}`.trim();

/**
 * Profilden kargo gönderme/alma özelliğini kullanabilecek çalışanların seçimi.
 * Her anahtar değişikliği Employees belgesine anında yazılır.
 */
const CargoAccessModal = ({ show, onHide }) => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    if (!show) return undefined;
    setLoading(true);
    return onSnapshot(
      collection(db, 'Employees'),
      (snapshot) => {
        const rows = snapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .filter(isActive)
          .sort((a, b) => fullName(a).localeCompare(fullName(b), 'tr'));
        setEmployees(rows);
        setLoading(false);
      },
      (error) => {
        console.error('Çalışanlar yüklenemedi:', error);
        toast.error('Çalışan listesi yüklenemedi');
        setLoading(false);
      }
    );
  }, [show]);

  const visible = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase('tr-TR');
    if (!needle) return employees;
    return employees.filter((employee) =>
      `${fullName(employee)} ${employee.eMail || ''}`.toLocaleLowerCase('tr-TR').includes(needle)
    );
  }, [employees, search]);

  if (!show) return null;

  const grantedCount = employees.filter((employee) => employee[CARGO_ACCESS_FIELD] === true).length;

  const toggle = async (employee) => {
    const next = employee[CARGO_ACCESS_FIELD] !== true;
    setSavingId(employee.id);
    try {
      await updateDoc(doc(db, 'Employees', employee.id), { [CARGO_ACCESS_FIELD]: next });
      toast.success(`${fullName(employee)}: kargo yetkisi ${next ? 'verildi' : 'kaldırıldı'}`);
    } catch (error) {
      console.error('Kargo yetkisi güncellenemedi:', error);
      toast.error('Yetki güncellenemedi');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className={styles.dialogOverlay} onClick={onHide}>
      <div
        className={`${styles.dialogCard} ${styles.dialogCardNarrow}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cargo-access-title"
      >
        <header className={styles.dialogHeader}>
          <div>
            <h2 id="cargo-access-title">Kargo Yetkileri</h2>
            <p className={styles.dialogSubtitle}>
              Seçilen çalışanlar profillerindeki <strong>Kargolarım</strong> sekmesinden kargo
              gönderip teslim alabilir. {grantedCount} kişi yetkili.
            </p>
          </div>
          <button type="button" className={styles.squareButton} onClick={onHide} aria-label="Kapat">
            <FaTimes />
          </button>
        </header>

        <div className={styles.dialogBody}>
          <label className={styles.searchBox}>
            <FaSearch />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Çalışan ara..."
              aria-label="Çalışan ara"
            />
          </label>

          {loading ? (
            <p className={styles.mutedText}>Yükleniyor...</p>
          ) : visible.length === 0 ? (
            <p className={styles.mutedText}>Eşleşen çalışan bulunamadı.</p>
          ) : (
            <ul className={styles.accessList}>
              {visible.map((employee) => {
                const admin = isAdmin(employee);
                const granted = admin || employee[CARGO_ACCESS_FIELD] === true;
                return (
                  <li key={employee.id} className={styles.accessItem}>
                    <div className={styles.accessIdentity}>
                      <strong>{fullName(employee) || employee.eMail}</strong>
                      <small>{employee.eMail}</small>
                    </div>
                    {admin ? (
                      <span className={styles.adminTag}>
                        <FaUserShield /> Yönetici
                      </span>
                    ) : (
                      <button
                        type="button"
                        role="switch"
                        aria-checked={granted}
                        aria-label={`${fullName(employee)} kargo yetkisi`}
                        className={`${styles.switch} ${granted ? styles.switchOn : ''}`}
                        onClick={() => toggle(employee)}
                        disabled={savingId === employee.id}
                      >
                        <span />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default CargoAccessModal;
